import React, { useEffect, useState } from 'react';
import { Loader, Message, Icon } from 'semantic-ui-react';
import { buildApiHostUrl } from '../utils/apiConfig';

/**
 * PublicFileAccess Component
 * Handles public file access via /f/{code} URLs without authentication
 *
 * Features:
 * - Automatic download via hidden iframe (page stays open)
 * - Backend sets Content-Disposition: attachment header to force download
 * - No CORS issues (iframe navigation, not XHR/fetch)
 * - Works with large files (1GB+) - streams directly from S3
 * - Manual download fallback button
 * - Production-ready approach used by major file sharing services
 */
const PublicFileAccess = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);

  useEffect(() => {
    const initiateDownload = () => {
      // Extract file code from URL path
      const pathParts = window.location.pathname.split('/');
      const fileCode = pathParts[pathParts.length - 1];

      if (!fileCode) {
        setError('Invalid file link');
        setLoading(false);
        return;
      }

      const downloadUrl = buildApiHostUrl(`f/${encodeURIComponent(fileCode)}`);

      // Store URL for display
      setFileInfo({
        filename: fileCode,
        downloadUrl: downloadUrl,
        success: true
      });

      // Trigger automatic download using anchor element
      // Since backend sets Content-Disposition: attachment, this will download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Show success message after brief delay
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    };

    initiateDownload();
  }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <Loader active size="large">Starting download...</Loader>
        <p style={{ marginTop: '20px', color: '#666' }}>
          Your download should begin shortly
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5',
        padding: '20px'
      }}>
        <Message negative size="large" style={{ maxWidth: '500px' }}>
          <Message.Header>
            <Icon name="warning circle" />
            File Access Error
          </Message.Header>
          <p>{error}</p>
        </Message>
        <a
          href="/"
          style={{
            marginTop: '20px',
            color: '#2185d0',
            textDecoration: 'none',
            fontSize: '16px'
          }}
        >
          <Icon name="home" />
          Return to Home
        </a>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundColor: '#f5f5f5',
      padding: '20px'
    }}>
      <Message positive size="large" style={{ maxWidth: '600px' }}>
        <Message.Header>
          <Icon name="check circle" />
          Download Started
        </Message.Header>
        <p style={{ marginTop: '15px' }}>
          Your file download should start automatically.
        </p>
        <p style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
          If the download doesn't start, click the button below:
        </p>
        <div style={{ textAlign: 'center', marginTop: '15px' }}>
          <a
            href={fileInfo?.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              padding: '10px 25px',
              backgroundColor: '#2185d0',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '5px',
              fontSize: '14px',
              fontWeight: 'bold',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#1678c2'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#2185d0'}
          >
            <Icon name="download" />
            Manual Download
          </a>
        </div>
      </Message>
      <a
        href="/"
        style={{
          marginTop: '20px',
          color: '#2185d0',
          textDecoration: 'none',
          fontSize: '16px'
        }}
      >
        <Icon name="home" />
        Return to Home
      </a>
    </div>
  );
};

export default PublicFileAccess;
